import React, { useMemo, useState } from "react";
import {
  Grid,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { DateTimePicker } from "@material-ui/pickers";
import clsx from "clsx";
import moment from "moment";
import { useStyles } from "./index.styles";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
  variant: "menu",
  getContentAnchorEl: null,
};

const LPRSearchFilters = ({
  filters,
  availableDevices,
  onFiltersChange,
  onSearch,
  isSearchEnabled,
  isLoading,
}) => {
  const classes = useStyles();

  // Track which date field was last modified to show error on the correct one
  const [lastChangedDate, setLastChangedDate] = useState(null);

  // Validation state derived from filters
  const validation = useMemo(() => {
    const endOfToday = moment().endOf("day");
    const startAfterToday = filters.startDateTime && moment.isMoment(filters.startDateTime) && filters.startDateTime.isAfter(endOfToday);
    const endAfterToday = filters.endDateTime && moment.isMoment(filters.endDateTime) && filters.endDateTime.isAfter(endOfToday);
    const startAfterEnd =
      filters.startDateTime &&
      filters.endDateTime &&
      moment.isMoment(filters.startDateTime) &&
      moment.isMoment(filters.endDateTime) &&
      filters.startDateTime.isValid() &&
      filters.endDateTime.isValid() &&
      filters.startDateTime.isSameOrAfter(filters.endDateTime);
    const noDeviceSelected = filters.selectedDevices.length === 0;

    // Highlight only the field that was last changed when there's a date order conflict
    const startHasError = startAfterToday || (startAfterEnd && lastChangedDate === "start");
    const endHasError = endAfterToday || (startAfterEnd && lastChangedDate === "end");

    return {
      startError: startHasError,
      endError: endHasError,
      dateHelperText: startAfterEnd
        ? (lastChangedDate === "start" ? "Start Date must be before End Date" : "End Date must be after Start Date")
        : startAfterToday
        ? "Start Date cannot be in the future"
        : endAfterToday
        ? "End Date cannot be in the future"
        : "",
      deviceError: noDeviceSelected,
    };
  }, [filters.startDateTime, filters.endDateTime, filters.selectedDevices, lastChangedDate]);

  const handleStartDateChange = (date) => {
    setLastChangedDate("start");
    onFiltersChange({
      ...filters,
      startDateTime: date,
    });
  };

  const handleEndDateChange = (date) => {
    setLastChangedDate("end");
    onFiltersChange({
      ...filters,
      endDateTime: date,
    });
  };

  const handlePlateTextChange = (e) => {
    const value = e.target.value;
    if (value.length <= 20) {
      onFiltersChange({
        ...filters,
        plateText: value,
      });
    }
  };

  const handleDeviceChange = (e) => {
    const value = e.target.value;
    // If undefined is in the array, it means the "Select All" MenuItem (which has no value) was clicked
    if (value.includes(undefined)) return;
    onFiltersChange({
      ...filters,
      selectedDevices: value,
    });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isSearchEnabled) {
      onSearch();
    }
  };

  const getDeviceName = (deviceId) => {
    const device = availableDevices.find((d) => d.deviceId === deviceId);
    return device ? device.deviceName : deviceId;
  };

  return (
    <Grid
      container
      className={clsx(classes.filtersContainer, "lpr-search-filters")}
      onKeyDown={handleKeyDown}
    >
      <Grid item xs={12} sm={6} md={3} className={classes.filterItem}>
        <div className={validation.startError ? classes.dateErrorWrapper : undefined}>
          <DateTimePicker
            label="Start Date/Time"
            value={filters.startDateTime}
            onChange={handleStartDateChange}
            inputVariant="outlined"
            format="MM/DD/YYYY hh:mm A"
            fullWidth
            autoOk
            maxDate={moment().endOf("day")}
            error={validation.startError}
            helperText=""
            className={clsx("lpr-start-date-picker")}
            data-testid="lpr-start-date-picker"
          />
          {validation.startError && validation.dateHelperText && (
            <Typography className={classes.dateErrorText} data-testid="lpr-date-error">
              {validation.dateHelperText}
            </Typography>
          )}
        </div>
      </Grid>

      <Grid item xs={12} sm={6} md={3} className={classes.filterItem}>
        <div className={validation.endError ? classes.dateErrorWrapper : undefined}>
          <DateTimePicker
            label="End Date/Time"
            value={filters.endDateTime}
            onChange={handleEndDateChange}
            inputVariant="outlined"
            format="MM/DD/YYYY hh:mm A"
            fullWidth
            autoOk
            maxDate={moment().endOf("day")}
            error={validation.endError}
            helperText=""
            className={clsx("lpr-end-date-picker")}
            data-testid="lpr-end-date-picker"
          />
          {validation.endError && validation.dateHelperText && (
            <Typography className={classes.dateErrorText} data-testid="lpr-date-error">
              {validation.dateHelperText}
            </Typography>
          )}
        </div>
      </Grid>

      <Grid item xs={12} sm={6} md={2} className={classes.filterItem}>
        <div className={validation.deviceError ? classes.dateErrorWrapper : undefined}>
          <FormControl variant="outlined" fullWidth>
            <InputLabel shrink id="lpr-device-select-label">
              Device Names
            </InputLabel>
            <Select
              labelId="lpr-device-select-label"
              id="lpr-device-select"
              multiple
              displayEmpty
              value={filters.selectedDevices}
              onChange={handleDeviceChange}
              input={<OutlinedInput notched label="Device Names" />}
              renderValue={(selected) => {
                if (selected.length === 0) return "Device Name";
                return selected.map((id) => getDeviceName(id)).join(", ");
              }}
              MenuProps={MenuProps}
              className={clsx(
                validation.deviceError ? classes.deviceSelectError : classes.deviceSelect,
                "lpr-device-select"
              )}
              data-testid="lpr-device-select"
            >
              <MenuItem disabled className={classes.deviceHeader}>
                Device Name
              </MenuItem>
              {filters.selectedDevices.length < availableDevices.length && (
                <MenuItem
                  onClick={() => {
                    onFiltersChange({ ...filters, selectedDevices: availableDevices.map((d) => d.deviceId) });
                  }}
                  className={clsx("lpr-select-all")}
                  data-testid="lpr-select-all"
                >
                  <ListItemText primary="Select All" />
                </MenuItem>
              )}
              {availableDevices.map((device) => (
                <MenuItem key={device.deviceId} value={device.deviceId}>
                  <Checkbox
                    checked={filters.selectedDevices.indexOf(device.deviceId) > -1}
                  />
                  <ListItemText primary={device.deviceName} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {validation.deviceError && (
            <Typography className={classes.dateErrorText}>
              Select a device
            </Typography>
          )}
        </div>
      </Grid>

      <Grid item xs={12} sm={6} md={2} className={classes.filterItem}>
        <TextField
          label="Plate Text"
          value={filters.plateText}
          onChange={handlePlateTextChange}
          variant="outlined"
          fullWidth
          inputProps={{ maxLength: 20 }}
          className={clsx("lpr-plate-text-input")}
          data-testid="lpr-plate-text-input"
        />
      </Grid>

      <Grid item xs={12} sm={6} md={2} className={classes.filterItem}>
        <Button
          variant="contained"
          color="primary"
          onClick={onSearch}
          disabled={!isSearchEnabled || isLoading}
          fullWidth
          className={clsx(classes.searchButton, "lpr-search-button")}
          data-testid="lpr-search-button"
        >
          {isLoading ? <CircularProgress size={24} color="inherit" /> : "SEARCH"}
        </Button>
      </Grid>
    </Grid>
  );
};

export default LPRSearchFilters;
